import React, { useState, useEffect } from 'react';
import { X, Save, RefreshCw, AlertCircle, Image as ImageIcon, Video, Camera } from 'lucide-react';
import { Button } from '@/components/ui';
import { AssetUpload } from '../hooks/usePhotoUpload';
import { AssetSource, AssetType, ImpactPhoto } from '../types/photo';
import { validateAsset } from '../utils/assetValidation';

interface AssetEditorModalProps {
  assets: Array<{
    photoId: string;
    photoData?: ImpactPhoto;
  }>;
  onSave: (editedAssets: AssetUpload[]) => void;
  onClose: () => void;
  generateTitle: (photoId: string, photoData?: any) => string;
  generateDescription: (photoId: string, photoData?: any) => string;
  detectAssetType: (photoId: string, mimeType?: string) => AssetType;
}

interface EditableAsset extends AssetUpload {
  photoData?: ImpactPhoto;
  validationErrors?: string[];
}

const SOURCE_OPTIONS: { value: AssetSource; label: string; description: string }[] = [
  { value: 'Impact', label: 'Impact Photos', description: 'Photos from Impact App' },
  { value: 'Brandfolder', label: 'Brandfolder Photos', description: 'Photos from Brandfolder DAM' }
];

const TYPE_OPTIONS: { value: AssetType; label: string; icon: React.ComponentType; description: string }[] = [
  { value: 'Image', label: 'Image', icon: ImageIcon, description: 'Still image file' },
  { value: 'Video', label: 'Video', icon: Video, description: 'Video file' }
];

export const AssetEditorModal: React.FC<AssetEditorModalProps> = ({
  assets,
  onSave,
  onClose,
  generateTitle,
  generateDescription,
  detectAssetType
}) => {
  const [editableAssets, setEditableAssets] = useState<EditableAsset[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize editable assets
  useEffect(() => {
    if (assets.length === 0) {
      setEditableAssets([]);
      return;
    }
    
    const initialized = assets.map(({ photoId, photoData }) => {
      const title = photoData?.title || generateTitle(photoId, photoData);
      const description = photoData?.description || generateDescription(photoId, photoData);
      
      return {
        id: photoId,
        title,
        description,
        source: (photoData?.source || 'Impact') as AssetSource,
        type: (photoData?.type || detectAssetType(photoId)) as AssetType,
        photoData,
        validationErrors: []
      };
    });
    
    setEditableAssets(initialized);
  }, [assets, generateTitle, generateDescription, detectAssetType]);

  // Validate asset and update errors
  const validateAndUpdateAsset = (index: number, updatedAsset: EditableAsset) => {
    const validationErrors = validateAsset(updatedAsset);
    const errorMessages = validationErrors.map(error => error.message);
    
    const newAssets = [...editableAssets];
    newAssets[index] = { ...updatedAsset, validationErrors: errorMessages };
    setEditableAssets(newAssets);
    setHasChanges(true);
  };

  // Update single asset field
  const updateAssetField = (index: number, field: keyof AssetUpload, value: string) => {
    const updatedAsset = { ...editableAssets[index], [field]: value };
    validateAndUpdateAsset(index, updatedAsset);
  };

  // Auto-generate content for an asset
  const autoGenerate = (index: number, field: 'title' | 'description') => {
    const asset = editableAssets[index];
    const value = field === 'title' 
      ? generateTitle(asset.id, asset.photoData) 
      : generateDescription(asset.id, asset.photoData);
    
    updateAssetField(index, field, value);
  };

  // Auto-detect type
  const autoDetectType = (index: number) => {
    const asset = editableAssets[index];
    const detectedType = detectAssetType(asset.id);
    updateAssetField(index, 'type', detectedType);
  };

  // Check if all assets are valid
  const allAssetsValid = editableAssets.every(asset => 
    !asset.validationErrors || asset.validationErrors.length === 0
  );

  // Handle save
  const handleSave = () => {
    if (!allAssetsValid) return;
    
    const cleanAssets: AssetUpload[] = editableAssets.map(({ 
      id, title, description, source, type 
    }) => ({
      id,
      title,
      description: description || undefined,
      source,
      type
    }));
    
    onSave(cleanAssets);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Edit Asset Metadata</h2>
            <p className="text-sm text-gray-600 mt-1">
              Customize titles, descriptions, source, and type for {editableAssets.length} asset{editableAssets.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          <div className="space-y-6">
            {editableAssets.map((asset, index) => (
              <div key={asset.id} className="border border-gray-200 rounded-lg p-4">
                {/* Asset Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    {asset.photoData?.asset_url ? (
                      <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                        <img
                          src={asset.photoData.asset_url}
                          alt={asset.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.className = 'w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0';
                              parent.innerHTML = `
                                <div class="w-full h-full flex items-center justify-center">
                                  <svg class="w-8 h-8 text-gray-400" stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round">
                                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
                                    <circle cx="12" cy="13" r="4"></circle>
                                  </svg>
                                </div>
                              `;
                            }
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-20 h-20 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                        <Camera className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900">Asset {index + 1}</h3>
                      <p className="text-sm text-gray-500 truncate">ID: {asset.id}</p>
                      {asset.photoData && (
                        <p className="text-xs text-gray-400">
                          {asset.photoData.program} • {asset.photoData.activity}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Validation Errors */}
                  {asset.validationErrors && asset.validationErrors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-2">
                      <div className="flex items-center">
                        <AlertCircle className="h-4 w-4 text-red-500 mr-1" />
                        <span className="text-xs text-red-700">
                          {asset.validationErrors.length} error{asset.validationErrors.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <ul className="text-xs text-red-600 mt-1 space-y-1">
                        {asset.validationErrors.map((error, i) => (
                          <li key={i}>• {error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Title */}
                  <div className="col-span-2">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Title *
                      </label>
                      <button
                        onClick={() => autoGenerate(index, 'title')}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Auto-generate
                      </button>
                    </div>
                    <input
                      type="text"
                      value={asset.title}
                      onChange={(e) => updateAssetField(index, 'title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter asset title"
                      maxLength={255}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {asset.title.length}/255 characters
                    </p>
                  </div>

                  {/* Description */}
                  <div className="col-span-2">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <button
                        onClick={() => autoGenerate(index, 'description')}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Auto-generate
                      </button>
                    </div>
                    <textarea
                      value={asset.description || ''}
                      onChange={(e) => updateAssetField(index, 'description', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter asset description (optional)"
                      maxLength={1000}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {(asset.description || '').length}/1000 characters
                    </p>
                  </div>

                  {/* Source */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Source
                    </label>
                    <select
                      value={asset.source}
                      onChange={(e) => updateAssetField(index, 'source', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {SOURCE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {SOURCE_OPTIONS.find(opt => opt.value === asset.source)?.description}
                    </p>
                  </div>

                  {/* Type */}
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Type
                      </label>
                      <button
                        onClick={() => autoDetectType(index)}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Auto-detect
                      </button>
                    </div>
                    <select
                      value={asset.type}
                      onChange={(e) => updateAssetField(index, 'type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {TYPE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {TYPE_OPTIONS.find(opt => opt.value === asset.type)?.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <div className="flex items-center space-x-4">
            {!allAssetsValid && (
              <div className="flex items-center text-red-600">
                <AlertCircle className="h-4 w-4 mr-2" />
                <span className="text-sm">Please fix validation errors before saving</span>
              </div>
            )}
            {hasChanges && allAssetsValid && (
              <div className="text-sm text-green-600">
                All changes look good!
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              variant="secondary"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!allAssetsValid || !hasChanges}
            >
              <Save className="h-4 w-4 mr-2" />
              Save & Upload ({editableAssets.length})
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};